import { Building2, Clock, Wallet as WalletIcon, ArrowUpRight, Info, ExternalLink, Globe2, Bitcoin, Coins } from 'lucide-react';
import { motion } from 'framer-motion';
import type { AttributionResult } from '@/types/walletIntel';

function formatBalance(raw: string, decimals: number): string {
  const n = Number(raw) / Math.pow(10, decimals);
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(2)}K`;
  return n.toFixed(6);
}

function formatDate(iso: string | null): string {
  if (!iso) return 'unknown';
  return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

function ageFrom(iso: string | null): string {
  if (!iso) return '—';
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000);
  const years = Math.floor(days / 365);
  if (years >= 1) return `~${years} year${years > 1 ? 's' : ''} old`;
  const months = Math.floor(days / 30);
  if (months >= 1) return `~${months} month${months > 1 ? 's' : ''} old`;
  return `~${days} day${days === 1 ? '' : 's'} old`;
}

// A handful of well-known exchange/entity names get their own accent color —
// everything else falls back to a generic "known entity" treatment. This
// isn't meant to be exhaustive, just to make the most commonly-seen labels
// (which dominate real screening traffic) visually distinct at a glance.
const ENTITY_COLORS: Record<string, string> = {
  binance: '#F0B429', coinbase: '#2979FF', kraken: '#8b5cf6', okx: '#000000',
  huobi: '#00C853', bitfinex: '#00C8FF', bybit: '#F0B429', kucoin: '#00C853',
  gemini: '#2979FF', 'crypto.com': '#0d1421',
};
function entityColor(label: string): string {
  const key = label.toLowerCase();
  for (const [name, color] of Object.entries(ENTITY_COLORS)) {
    if (key.includes(name)) return color;
  }
  return '#00C8FF';
}

function KpiCard({ icon: Icon, color, label, value, sub, delay }: {
  icon: typeof Clock; color: string; label: string; value: string; sub?: string; delay: number;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25, delay }}
      style={{
        background: `linear-gradient(160deg, ${color}12, #0C1118 60%)`,
        border: `1px solid ${color}30`, borderRadius: 12, padding: '14px 16px',
      }}
    >
      <div className="flex items-center gap-2 mb-2.5">
        <div style={{ width: 28, height: 28, borderRadius: 8, background: `${color}20`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Icon size={14} style={{ color }} />
        </div>
        <span style={{ color: '#8D96AC', fontSize: 11, letterSpacing: '0.06em', fontWeight: 600 }}>{label}</span>
      </div>
      <div style={{ color: '#F2F6FC', fontFamily: "'Space Grotesk', monospace", fontWeight: 800, fontSize: 20 }}>{value}</div>
      {sub && <div style={{ color: '#5A7A9A', fontSize: 11, marginTop: 4 }}>{sub}</div>}
    </motion.div>
  );
}

interface Props {
  attribution: AttributionResult;
}

export function IdentityPanel({ attribution }: Props) {
  const { info, breadcrumbs, multichain, networks } = attribution;
  const labels = [...new Set(breadcrumbs.map((b) => b.entity_label).filter(Boolean))] as string[];
  const tags = [...new Set(breadcrumbs.flatMap((b) => b.entity_tags))];
  const primaryColor = labels[0] ? entityColor(labels[0]) : '#00C8FF';

  return (
    <div className="flex flex-col gap-4">
      {/* What entity owns this — hero card */}
      <motion.div
        initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25 }}
        style={{
          background: labels.length ? `linear-gradient(135deg, ${primaryColor}14, #0C1118 55%)` : '#0C1118',
          border: `1px solid ${labels.length ? primaryColor + '35' : '#182030'}`,
          borderRadius: 14, padding: '18px 20px',
        }}
      >
        <p className="tracking-[0.1em] mb-3.5" style={{ color: '#7C8CA6', fontSize: 11.5, fontWeight: 700 }}>KNOWN ENTITY / EXCHANGE</p>
        {labels.length > 0 ? (
          <div className="flex items-center gap-4 flex-wrap">
            <div style={{
              width: 48, height: 48, borderRadius: 13, background: `${primaryColor}22`, border: `1.5px solid ${primaryColor}50`,
              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            }}>
              <Building2 size={22} style={{ color: primaryColor }} />
            </div>
            <div className="flex flex-col gap-2 min-w-0">
              <span style={{ color: '#F2F6FC', fontFamily: "'Space Grotesk', monospace", fontWeight: 800, fontSize: 20 }}>{labels[0]}</span>
              <div className="flex items-center gap-1.5 flex-wrap">
                {tags.map((t) => (
                  <span key={t} style={{ background: `${primaryColor}18`, color: primaryColor, border: `1px solid ${primaryColor}30`, fontSize: 10 }}
                    className="font-bold px-2.5 py-1 rounded-full tracking-wider uppercase">{t}</span>
                ))}
                {labels.length > 1 && (
                  <span style={{ color: '#5A7A9A', fontSize: 11.5 }}>+{labels.length - 1} more label{labels.length > 2 ? 's' : ''}</span>
                )}
              </div>
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-3">
            <div style={{ width: 48, height: 48, borderRadius: 13, background: '#182030', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <WalletIcon size={20} style={{ color: '#5A7A9A' }} />
            </div>
            <p style={{ color: '#8D96AC', fontSize: 13 }}>
              No known entity/exchange label — behaves like an unlabeled personal wallet in observed on-chain activity.
            </p>
          </div>
        )}
      </motion.div>

      {/* Critical: clearweb / external mentions — moved up, high-contrast, real cards */}
      {networks.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25, delay: 0.05 }}
          style={{ background: '#0C1118', border: '1px solid #F0B42935', borderLeft: '3px solid #F0B429', borderRadius: 12, padding: '16px 18px' }}
        >
          <div className="flex items-center gap-2 mb-3.5">
            <Globe2 size={16} style={{ color: '#F0B429' }} />
            <p style={{ color: '#F0B429', fontWeight: 700, fontSize: 13, letterSpacing: '0.03em' }}>
              MENTIONED IN {networks.length} EXTERNAL REPORT{networks.length === 1 ? '' : 'S'} — CROWD-SOURCED, UNVERIFIED, WORTH A LOOK
            </p>
          </div>
          <div className="flex flex-col gap-2 max-h-64 overflow-y-auto pr-1">
            {networks.slice(0, 20).map((n, i) => (
              <a
                key={i} href={n.report_link} target="_blank" rel="noreferrer"
                className="flex items-center justify-between gap-3 group"
                style={{ background: '#050709', border: '1px solid #182030', borderRadius: 9, padding: '10px 13px' }}
              >
                <div className="min-w-0 flex flex-col">
                  <span style={{ color: '#DCE8F5', fontSize: 13, fontWeight: 600 }} className="truncate group-hover:underline">
                    {n.report_title}
                  </span>
                  {n.report_owner && (
                    <span style={{ color: '#8D96AC', fontSize: 11, marginTop: 3 }}>by {n.report_owner}</span>
                  )}
                </div>
                <ExternalLink size={13} style={{ color: '#00C8FF', flexShrink: 0 }} />
              </a>
            ))}
          </div>
          {networks.length > 20 && (
            <p style={{ color: '#8D96AC', fontSize: 11, marginTop: 9 }}>+{networks.length - 20} more not shown</p>
          )}
          <p style={{ color: '#5A7A9A', fontSize: 11.5, marginTop: 11, lineHeight: 1.55 }}>
            These are user-submitted investigation reports that reference this address — not confirmation of
            wrongdoing on their own, but a real lead: someone else has already looked at this wallet for a reason.
          </p>
        </motion.div>
      )}

      {/* KPIs: age, balance, tx counts */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCard icon={Clock} color="#F0B429" label="FIRST SEEN" value={formatDate(info.first_transaction)} sub={ageFrom(info.first_transaction)} delay={0.1} />
        <KpiCard icon={Clock} color="#00C853" label="LAST SEEN" value={formatDate(info.last_transaction)} delay={0.14} />
        <KpiCard icon={WalletIcon} color="#2979FF" label="BALANCE" value={formatBalance(info.balance, info.decimals)} delay={0.18} />
        <KpiCard icon={ArrowUpRight} color="#F0B429" label="TOTAL TXNS" value={info.total_tx_count.toLocaleString()}
          sub={`${info.incoming_tx_count.toLocaleString()} in / ${info.outgoing_tx_count.toLocaleString()} out`} delay={0.22} />
      </div>

      {/* Multichain presence */}
      {multichain.some((m) => m.total_tx_count > 0) && (
        <div style={{ background: '#0C1118', border: '1px solid #182030', borderRadius: 12, padding: '16px 18px' }}>
          <div className="flex items-center gap-2 mb-3.5">
            <Coins size={15} style={{ color: '#7C8CA6' }} />
            <p className="tracking-[0.1em]" style={{ color: '#7C8CA6', fontSize: 11.5, fontWeight: 700 }}>SAME ADDRESS, OTHER CHAINS</p>
          </div>
          <div className="flex flex-wrap gap-2">
            {multichain.filter((m) => m.total_tx_count > 0).map((m) => (
              <span key={m.chain} style={{ background: '#0F2030', border: '1px solid #1E3A5F', borderRadius: 9, padding: '7px 13px' }}
                className="flex items-center gap-2" >
                <Bitcoin size={13} style={{ color: '#F0B429' }} />
                <span style={{ color: '#DCE8F5', fontWeight: 700, fontSize: 12.5 }}>{m.chain}</span>
                <span style={{ color: '#8D96AC', fontSize: 12 }}>{m.total_tx_count.toLocaleString()} txns</span>
              </span>
            ))}
          </div>
        </div>
      )}

      {/* What can't be answered */}
      <div style={{ background: '#0C1118', border: '1px solid #182030', borderRadius: 12, padding: '15px 18px', display: 'flex', gap: 11 }}>
        <Info size={16} style={{ color: '#7C8CA6', flexShrink: 0, marginTop: 1 }} />
        <p style={{ color: '#8D96AC', fontSize: 12.5, lineHeight: 1.6 }}>
          On-chain addresses don't carry HD-derivation metadata (parent address, derivation path, "merkle tree") on the chain itself —
          that's wallet-software-side bookkeeping, not something any blockchain explorer or API can answer for an arbitrary address.
          What <em>is</em> derivable from real usage patterns is shown above (age, exchange attribution, activity) and in the
          Relationships tab (which wallets this one actually clusters with).
        </p>
      </div>
    </div>
  );
}
